import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ResolvedBlogPost } from '../../types/blog';
import { formatPostDate } from '../../lib/blog';
import { useLocalizedPath } from '../../i18n/useLocalizedPath';

interface PostCardProps {
  post: ResolvedBlogPost;
  /** Variação compacta sem imagem (usada em "related posts" no fim do post). */
  compact?: boolean;
}

export function PostCard({ post, compact = false }: PostCardProps) {
  const { t } = useTranslation('blog');
  const path = useLocalizedPath();
  const href = `${path('blog')}/${post.slug}`;
  const category = t(`category.${post.category}`, post.category);

  if (compact) {
    return (
      <Link
        to={href}
        className="group block bg-white border border-gray-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-md transition-all"
      >
        <span className="text-[10px] font-semibold text-primary-600 uppercase tracking-widest">
          {category}
        </span>
        <h3 className="mt-2 text-sm font-bold text-gray-900 leading-snug group-hover:text-primary-700 transition-colors">
          {post.title}
        </h3>
        <p className="mt-2 text-xs text-gray-500 line-clamp-2">{post.excerpt}</p>
        <p className="mt-3 text-[10px] text-gray-400">
          {formatPostDate(post.publishedAt, post.locale)} ·{' '}
          {t('card.read_time_short', '{{min}} min', { min: post.readTimeMinutes })}
        </p>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className="group block bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-primary-300 hover:shadow-lg transition-all"
    >
      <div className="aspect-[975/576] overflow-hidden bg-gray-100">
        <img
          src={post.coverImage}
          alt={post.coverImageAlt}
          width={975}
          height={576}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-5">
        <span className="text-[10px] font-semibold text-primary-600 uppercase tracking-widest">
          {category}
        </span>
        <h3 className="mt-2 text-base font-bold text-gray-900 leading-snug group-hover:text-primary-700 transition-colors">
          {post.title}
        </h3>
        <p className="mt-2 text-sm text-gray-500 line-clamp-2">{post.excerpt}</p>
        <p className="mt-4 text-xs text-gray-400">
          {t('card.by', 'Por')} {post.author} ·{' '}
          {formatPostDate(post.publishedAt, post.locale)} ·{' '}
          {t('card.read_time', '{{min}} min de leitura', { min: post.readTimeMinutes })}
        </p>
      </div>
    </Link>
  );
}

export default PostCard;
